import moment from 'moment'
import * as React from 'react'
import { useCallback, useContext, useEffect, useState } from 'react'
import { Route, Switch, useRouteMatch, useLocation } from 'react-router'
import { Redirect } from 'react-router-dom'
import useFetch, { CachePolicies } from 'use-http'
import { DateSelector, SideMenu } from '../../components/Analytics'
import type { AnalyticsAPIResponse } from '../../interfaces'
import { BaseLayout } from '../../layouts'
import { MainContentHeadingBox } from '../../layouts/Base/MainContent'
import { UserContext } from '../../providers'
import { ErrorReportsPage, MediaUsagePage, UsageAnalyticsPage } from './index'
import { HStack, IconButton, Tooltip } from '@chakra-ui/react'
import { FiGrid, FiList } from 'react-icons/fi'

const DATE_90_DAYS_ERROR = 'You can select a maximum range of 90 days!'
const DATE_BAD_ORDER_ERROR = 'Start date cannot be greater than the end date'

// TODO: use toLocaleDateString when displaying date
// const dateOptions = { year: 'numeric', month: 'long', day: 'numeric' }

const formatDate = (date: Date): string => moment(date).format()

const subtractDates = (date1, date2): number => {
  const diffTime = Math.abs(date2 - date1)
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
  return diffDays + 1
}

const AnalyticsRouter = (): React.ReactElement => {
  const { path } = useRouteMatch()
  const location = useLocation()

  const { user } = useContext(UserContext)

  const { request } = useFetch<AnalyticsAPIResponse>({
    cachePolicy: CachePolicies.NO_CACHE,
  })

  const getData = async (
    formattedStartDate: string,
    formattedEndDate: string,
  ): Promise<void> => {
    if (user?.clientNumber) {
      setIsRefreshing(true)
      setDisableApply(true)

      const response = await request.get(
        `/api/graphs/${user.clientNumber}/${formattedStartDate}/${formattedEndDate}`,
      )

      setAnalyticsData(response)
      setIsRefreshing(false)
      setDisableApply(false)
    }
  }

  /* *********************** STATE DEFINITIONS ***************** */

  const currentDate = moment().endOf('day').toDate()
  const firstDayOfMonth = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth(),
    1,
  )
  const [startDate, setStartDate] = useState(firstDayOfMonth)
  const [endDate, setEndDate] = useState(currentDate)

  const [submittedStartDate, setSubmittedStartDate] = useState(firstDayOfMonth)
  const [, setSubmittedEndDate] = useState(currentDate)

  const [, setDateError] = useState<string | null>(null)
  const [disableApply, setDisableApply] = useState(false)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [showBandwidth, setShowBandwidth] = useState(true)
  const [viewType, setViewType] = useState<'grid' | 'table'>('grid')

  const [data, setAnalyticsData] = useState<AnalyticsAPIResponse | null>(null)

  /* *********************** STATE DEFINITIONS END ***************** */

  /* *********************** EFFECT DEFINITIONS ***************** */

  useEffect(() => {
    void getData(formatDate(startDate), formatDate(endDate))
  }, [user])

  useEffect(() => {
    const thirteenthDecember = new Date('2020-12-13')
    if (submittedStartDate < thirteenthDecember) {
      setShowBandwidth(false)
    } else {
      setShowBandwidth(true)
    }
  }, [submittedStartDate])

  useEffect(() => {
    const difference = subtractDates(startDate, endDate)
    if (difference > 90) {
      setDateError(DATE_90_DAYS_ERROR)
      setDisableApply(true)
      return
    }

    if (startDate > endDate) {
      setEndDate(startDate)
    }

    setDateError(null)
    setDisableApply(false)
  }, [startDate])

  useEffect(() => {
    if (startDate > endDate) {
      setDateError(DATE_BAD_ORDER_ERROR)
      setDisableApply(true)
      return
    }

    const difference = subtractDates(startDate, endDate)
    if (difference > 90) {
      setDateError(DATE_90_DAYS_ERROR)
      setDisableApply(true)
      return
    }
    setDateError(null)
    setDisableApply(false)
  }, [endDate])

  /* *********************** EFFECT DEFINITIONS END ***************** */

  /* *********************** CALLBACK DEFINITIONS ***************** */
  const handleDateSubmit = useCallback(() => {
    setSubmittedEndDate(endDate)
    setSubmittedStartDate(startDate)

    void getData(formatDate(startDate), formatDate(endDate))
  }, [startDate, endDate])
  /* *********************** CALLBACK DEFINITIONS END ***************** */

  const isMediaUsagePage = location.pathname.includes('/media-usage')

  return (
    <BaseLayout menu={<SideMenu />}>
      <MainContentHeadingBox>
        <HStack spacing={4} width="100%" justifyContent="space-between">
          <DateSelector
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
            handleDateSubmit={handleDateSubmit}
            disabled={disableApply || isRefreshing}
            isRefreshing={isRefreshing}
          />
          {isMediaUsagePage && (
            <HStack spacing={2}>
              <Tooltip label="Grid View">
                <IconButton
                  aria-label="Grid view"
                  icon={<FiGrid />}
                  size="md"
                  variant={viewType === 'grid' ? 'solid' : 'ghost'}
                  colorScheme={viewType === 'grid' ? 'blue' : 'gray'}
                  onClick={() => setViewType('grid')}
                />
              </Tooltip>
              <Tooltip label="Table View">
                <IconButton
                  aria-label="Table view"
                  icon={<FiList />}
                  size="md"
                  variant={viewType === 'table' ? 'solid' : 'ghost'}
                  colorScheme={viewType === 'table' ? 'blue' : 'gray'}
                  onClick={() => setViewType('table')}
                />
              </Tooltip>
            </HStack>
          )}
        </HStack>
      </MainContentHeadingBox>

      <Switch>
        <Route path={`${path}`} exact>
          <Redirect to={`${path}/overall-usage`} />
        </Route>

        <Route path={`${path}/overall-usage`} exact>
          <UsageAnalyticsPage
            {...{
              data,
              showBandwidth,
              dateRange: {
                startDate,
                endDate,
              },
            }}
          />
        </Route>

        <Route path={`${path}/media-usage`} exact>
          <MediaUsagePage {...{
            data,
            dateRange: {
              startDate,
              endDate,
            },
            viewType,
          }} />
        </Route>

        <Route path={`${path}/error-reports`} exact>
          <ErrorReportsPage {...{ data }} />
        </Route>
      </Switch>
    </BaseLayout>
  )
}

export default AnalyticsRouter
